import { useState, useEffect, useCallback } from 'react';
import AllowanceApplicationDomain, { AllowanceDetail } from 'domain/master/attend/attendAllowanceApplication';
import { useHistory } from 'react-router-dom';
import { getAllowanceApplication, postAllowanceApplication } from 'api/attendAllowanceApplication';
import { useFormik } from 'formik';
import useToastNotification from 'hooks/useToastNotification';

/** 申請値の上限（server の AttendAllowanceApplicationService と揃える） */
export const ALLOWANCE_VALUE_MAX = 99999999;

/** ボタンタイプ 0:申請 1:申請キャンセル 2:削除申請 */
export const BUTTON_TYPE_APPLY = 0;
export const BUTTON_TYPE_CANCEL = 1;
export const BUTTON_TYPE_DELETE = 2;

export const AllowanceApplicationForm = () => {
  const history = useHistory();

  const [isLoading, setLoading] = useState(false);
  const [targetDate] = useState(sessionStorage.getItem('application.targetDate') || '');
  const [selectStaffCode] = useState(sessionStorage.getItem('application.staffCode') || '');
  const [dispStaffName] = useState(sessionStorage.getItem('application.dispStaffName') || '');
  // 出勤組織は遷移元の画面が渡した組織で固定（画面では変更しない）
  const [selectOrgCode] = useState(sessionStorage.getItem('application.orgCode') || '');
  const [confirmApplyModalOpen, setConfirmApplyModalOpen] = useState(false);
  const [isGetAllowanceSuccess, setIsGetAllowanceSuccess] = useState(true);
  const { errorNotification } = useToastNotification();

  const onSubmit = async (values: AllowanceApplicationDomain) => {
    if (!confirmApplyModalOpen) {
      setConfirmApplyModalOpen(true);
      return;
    }

    const {
      modifierApplicationId, version, orgName, buttonType, applicationReason,
      staffCode, orgCode, targetDateStr, detailList,
    } = values.getRawData();

    const dataPost = {
      modifierApplicationId: modifierApplicationId || null,
      version: version || null,
      orgCode,
      orgName,
      staffCode,
      buttonType,
      targetDateStr,
      // 申請理由は任意入力（server 側でも未入力チェックはしない）
      applicationReason: applicationReason || null,
      updateUserCode: sessionStorage.getItem('loginUser.staffCode') || '',
      // 値が入力された行のみ送る
      detailList: (detailList || []).filter((d) => d.allowanceValue !== null
        && String(d.allowanceValue) !== ''),
    };

    try {
      setLoading(true);
      setConfirmApplyModalOpen(false);
      const response = await postAllowanceApplication(dataPost);
      if (!response.errors) {
        history.push(sessionStorage.getItem('attendStampList.returnDestination')! || '');
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setConfirmApplyModalOpen(false);
      if (error.response && error.response.data && error.response.data.errors) {
        const listErr = error.response.data.errors;
        let stringErr = '';
        listErr.forEach((element: any) => {
          stringErr += `${element.defaultMessage}<br />`;
        });
        errorNotification(stringErr);
      } else {
        errorNotification('サーバー側でエラーが発生しました。');
        throw error;
      }
    }
  };

  const formik = useFormik({
    initialValues: AllowanceApplicationDomain.generateInitial(),
    validationSchema: false,
    validateOnChange: false,
    onSubmit,
  });

  useEffect(() => {
    setLoading(true);
    getAllowanceApplication({
      orgCode: selectOrgCode,
      targetDate,
      staffCode: selectStaffCode,
    }).then((data: any) => {
      formik.setValues(new AllowanceApplicationDomain(data));
      setLoading(false);
    }).catch(() => {
      setIsGetAllowanceSuccess(false);
      setLoading(false);
      errorNotification('サーバー側でエラーが発生しました。');
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectOrgCode, selectStaffCode, targetDate]);

  /** 明細1行の値を更新する */
  const setDetailValue = useCallback((allowanceId: string, value: string) => {
    // 半角数字以外は受け付けない（全角数字・記号・文字を含む）。
    // Number() が NaN になり画面に "NaN" と出てしまうため、入力時点で止める。
    if (value !== '' && !/^[0-9]+$/.test(value)) {
      return;
    }
    // 上限を超える入力は受け付けない。server 側でも弾くが、申請してからエラーになるより入力時点で止める。
    if (value !== '' && Number(value) > ALLOWANCE_VALUE_MAX) {
      return;
    }
    const next = formik.values.detailList.map((d: AllowanceDetail) => {
      if (d.allowanceId !== allowanceId) {
        return d;
      }
      return {
        ...d,
        allowanceValue: value === '' ? null : Number(value),
      };
    });
    formik.setFieldValue('detailList', next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik]);

  /** 明細1行の備考を更新する */
  const setDetailComment = useCallback((allowanceId: string, comment: string) => {
    const next = formik.values.detailList.map((d: AllowanceDetail) => (
      d.allowanceId === allowanceId ? { ...d, comment } : d
    ));
    formik.setFieldValue('detailList', next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik]);

  const closeConfirmApplyModal = useCallback(() => {
    setConfirmApplyModalOpen(false);
  }, []);

  return {
    formik,
    selectOrgCode,
    targetDate,
    dispStaffName,
    confirmApplyModalOpen,
    setConfirmApplyModalOpen,
    closeConfirmApplyModal,
    setDetailValue,
    setDetailComment,
    isLoading,
    isGetAllowanceSuccess,
  };
};

export default AllowanceApplicationForm;
